import { isDevProdImportFrontendMode } from './feature-flags';

/**
 * Which Clerk instance and which Convex deployment this bundle talks to.
 *
 * The PROD-import mode reads *dedicated* variables rather than the usual
 * `VITE_CLERK_PUBLISHABLE_KEY` / `VITE_CONVEX_URL`, because those two cannot
 * be overridden from `.env.prod-import.local`:
 *
 *   1. Nx loads `apps/app/.env.local` into `process.env` for the
 *      `app:dev-prod-import` task (`task-env-paths.js`) — it knows nothing
 *      about the Vite *mode* `prod-import`, which is not an Nx configuration;
 *   2. Vite's `loadEnv` finishes with `for (const key in process.env) if
 *      (key.startsWith('VITE_')) env[key] = process.env[key]`, so `process.env`
 *      wins over `.env.<mode>.local`.
 *
 * A `VITE_CLERK_PUBLISHABLE_KEY` sitting in `.env.local` therefore wins
 * silently, and the bundle authenticates against the *dev* Clerk instance
 * while claiming to be the PROD-import build. PROD then rejects the forwarded
 * JWT ("No auth provider found matching the given token"). Prefixed names
 * never collide, so they cannot be shadowed. See `docs/SANDBOX_IMPORT.md`.
 */
export interface RuntimeEnv {
  isViteDevelopment: boolean;
  appEnv: string | undefined;
  clerkPublishableKey: string | undefined;
  convexUrl: string | undefined;
  prodImportClerkPublishableKey: string | undefined;
  prodImportConvexUrl: string | undefined;
}

export interface RuntimeConfig {
  clerkPublishableKey?: string;
  convexUrl?: string;
  /** Set when the mode's variables are unusable; nothing is returned with it. */
  error?: string;
}

const clean = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export function resolveRuntimeConfig(env: RuntimeEnv): RuntimeConfig {
  if (!isDevProdImportFrontendMode(env.isViteDevelopment, env.appEnv)) {
    return {
      clerkPublishableKey: clean(env.clerkPublishableKey),
      convexUrl: clean(env.convexUrl),
    };
  }

  const clerkPublishableKey = clean(env.prodImportClerkPublishableKey);
  const convexUrl = clean(env.prodImportConvexUrl);

  const missing = [
    clerkPublishableKey ? null : 'VITE_PROD_IMPORT_CLERK_PUBLISHABLE_KEY',
    convexUrl ? null : 'VITE_PROD_IMPORT_CONVEX_URL',
  ].filter((name): name is string => name !== null);
  if (missing.length > 0) {
    return {
      error: `VITE_APP_ENV=dev-prod-import requires ${missing.join(' and ')} in apps/app/.env.prod-import.local (see docs/SANDBOX_RUNBOOK.md).`,
    };
  }

  // Loud here rather than a 500 from PROD a minute later: a `pk_test_` key
  // mints JWTs no production deployment can verify, and the handshake dies
  // with `SANDBOX_EXPORT_INVALID_TOKEN`.
  if (!clerkPublishableKey?.startsWith('pk_live_')) {
    return {
      error: `VITE_PROD_IMPORT_CLERK_PUBLISHABLE_KEY must be a production Clerk key (pk_live_…); got "${clerkPublishableKey}". The PROD handshake rejects any other issuer.`,
    };
  }

  return { clerkPublishableKey, convexUrl };
}

/**
 * Vite inlines `import.meta.env` statically, so every var is read literally
 * here rather than looked up by key.
 */
export const RUNTIME_CONFIG = resolveRuntimeConfig({
  isViteDevelopment: import.meta.env.DEV,
  appEnv: import.meta.env.VITE_APP_ENV,
  clerkPublishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  convexUrl: import.meta.env.VITE_CONVEX_URL,
  prodImportClerkPublishableKey: import.meta.env
    .VITE_PROD_IMPORT_CLERK_PUBLISHABLE_KEY,
  prodImportConvexUrl: import.meta.env.VITE_PROD_IMPORT_CONVEX_URL,
});
